/**
 * Copy for "How this report is generated" on the Exec Sales Dashboard
 * (ExecSalesDashboardMethod.tsx). Keep it in step with
 * backend/src/services/salesDashboardData.js and dealActivity.js.
 */

export interface MethodSection {
  heading: string
  points: string[]
}

export const EXEC_DASHBOARD_METHOD: MethodSection[] = [
  {
    heading: 'Period and money',
    points: [
      'Revenue, deal counts and pipeline cover deals whose close date falls in the current fiscal year (1 March to the end of February). Comparisons with last year use the same stretch of the previous fiscal year, up to the same day.',
      'All amounts are in Canadian dollars, converted from each deal’s currency at the stored exchange rates.',
      'The “Service (Do not Use)” stage is a forecasting placeholder. It counts towards Weighted Pipeline only and is left out of every other figure.',
    ],
  },
  {
    heading: 'Company at a glance',
    points: [
      'Won Revenue and Won Deals are Closed Won deals this fiscal year. Win Rate is won deals divided by all closed deals (won and lost).',
      'Open Pipeline is the value of open deals closing this fiscal year, and Avg Deal Size is that value divided by the number of open deals.',
      'Weighted Pipeline is each deal’s amount multiplied by its probability, for every deal closing this fiscal year that is not Closed Lost. Won deals count in full.',
      'Bookings by month shows Closed Won revenue for the last 12 months. The current month is still in progress.',
    ],
  },
  {
    heading: 'Forecast',
    points: [
      'The forecast is open pipeline weighted by probability, placed in the month each deal is due to close, alongside what has already been won.',
      'The Software and Services views split deals by the product family of their line items. “All” also includes deals that have no line items.',
    ],
  },
  {
    heading: 'Needs attention',
    points: [
      'Activity means a completed task, a meeting that has taken place, a logged call, or an email sent or received. For emails only the date and whether it was sent or received are used: nobody can read another person’s email, admins included.',
      'Activity counts towards a deal when it is logged on the deal itself. Reps often log deal work on the account or one of its contacts instead, so that counts too when the deal’s owner did it, or when the account has only one open deal. A support call by someone else on a busy account does not make a quiet deal look active.',
      '“Via account” on a deal means its latest activity was logged on the account or a contact rather than on the deal. “Where reps log deal activity” shows, for each rep, how many of their recently active deals were logged each way.',
      'A stalled deal has had no activity for 30 days or more (90 or more is critical). The count starts from the latest activity. If a deal has none, it counts from the last activity Salesforce recorded before it was switched off in August 2026, and failing that from the date the deal was created.',
      'A deal has a next step when there is an open task on it (undated, or no more than 30 days overdue) or a meeting still to come. Tasks and meetings on the account count under the same owner rule.',
      'A stale account has had no activity for 90 days or more on the account, its contacts or any of its deals.',
      'An email-only account has had activity in the last 6 months, but all of it is email, on 6 or fewer days, with no calls, meetings or completed tasks. “No replies” means the customer never wrote back. Accounts with no activity at all are counted as stale instead.',
      'Renewals due are Closed Won contracts whose end date falls in the next 90 days.',
    ],
  },
  {
    heading: 'Rep leaderboard',
    points: [
      'Reps are ranked by Won Revenue this fiscal year. “% of co.” is their share of company won revenue, and Index compares them with the team average, where 100 is average.',
      'Activity is the number of completed tasks, meetings and calls the rep has logged this fiscal year, wherever they were logged.',
    ],
  },
  {
    heading: 'Follow-through',
    points: [
      'This tab shows what reps are not doing. Each card counts records that need a rep to act, and opens exactly those records.',
      'Leads nobody has contacted: open leads (New or Working) from the last 90 days with no call, email, meeting or completed task 2 or more working days after they arrived.',
      'Close date has passed: open deals whose expected close date is in the past. The forecast relies on these dates, so they need updating.',
      'Quotes not chased: a quote presented 7 or more days ago, with no activity on its deal or account since. Expiring counts quotes presented or approved that run out in the next 14 days.',
      'Renewals nobody is working: a Closed Won contract ending in the next 90 days, on an account with no activity for 30 days or more.',
      'Overdue tasks: open tasks more than 7 days past their due date (up to a year back).',
      'Only one contact: open deals worth $10,000 CAD or more where at most one person at the customer has been in touch in the last 6 months or is named on the deal.',
      'Busy but not moving: deals with activity in the last 30 days that have stayed in the same stage for 90 days or more.',
      'How each rep works shows, over the last 90 days, calls (every dial imported from the phone system, with the share answered), conversations (answered calls of 60 seconds or more), talk time, meetings, emails sent and tasks completed, and how many of the rep’s accounts in play (an open deal, or a contract still running) had any activity.',
    ],
  },
  {
    heading: 'Investigating a number',
    points: [
      'Click a card or a name to open the deals or accounts behind that number in a new tab, for example a rep’s stale accounts or their stalled deals. The list uses the same rules, so its count matches the dashboard.',
    ],
  },
  {
    heading: 'When the figures are worked out',
    points: [
      'Everything is calculated from live GRM data when this page opens. The daily Exec Sales Digest email uses the same calculations.',
    ],
  },
]
